import React, { useEffect, useRef, useState } from 'react'
import { Log, Resource, ResourceFile, ResourceFolder, User } from '../../utils/interfaces'
import { CloseIcon, ExecuteIcon, FileIcon, Settings, TerminalIcon, TerminateIcon } from '../../utils/svgs'
import { useEventListener } from '@iwbam/react-ez'
import CodeEditor from './CodeEditor'
import { copy, getContentFromBucket, getPrettyLogs, setupBuildSpec, uploadToBucket, useUnSavedChangesWarning } from '../../utils/functions'
import { databaseName } from '../../utils/constants'
import ConfigSettings from './ConfigSettings'
import { config } from '../../config'
import JarDownloadContent from './JarDownloadContent'
import Resizer from './Resizer'
import { extensionToIcon } from './ResourceFileButton'

interface Props {
  user: User
  resource: Resource
  setResource: React.Dispatch<React.SetStateAction<undefined | null | Resource>>
  openingFiles: ResourceFile[]
  setOpeningFiles: React.Dispatch<React.SetStateAction<ResourceFile[]>>
  closeFile: (id: string) => void
  selectedFile: ResourceFile | null
  setSelectedFile: React.Dispatch<React.SetStateAction<ResourceFile | null>>
  jarContent: ArrayBuffer | null
  loadJarContent: (user: User, resource: Resource) => Promise<void>
}

const ResourceEditor: React.FC<Props> = (props) => {

  const consoleRef = useRef(null);
  const resizerRef = useRef(null);
  const timerRef = useRef<NodeJS.Timer | null>(null);
  // navigator
  const [isDragging, setIsDragging] = useState(false);
  const [draggingFile, setDraggingFile] = useState<ResourceFile | null>(null);
  const [virtualFilePos, setVirtualFilePos] = useState({ top: -1000, left: -1000 });
  const [draggingIndex, setDraggingIndex] = useState(-1);
  const [hoverCloseButton, setHoverCloseButton] = useState('');
  // config
  const [mainClass, setMainClass] = useState(props.resource.mainClass);
  const [version, setVersion] = useState<string>(props.resource.version);
  const [description, setDescription] = useState(props.resource.description);
  const [isConfigUpdated, setIsConfigUpdated] = useState(false);
  // code
  const [originalContents, setOriginalContents] = useState<{ [key: string]: string }>({});
  const [contents, setContents] = useState<{ [key: string]: string }>({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [buildId, setBuildId] = useState('');
  // console
  const [logs, setLogs] = useState<Log[]>([]);
  const [isShowingConsole, setIsShowingConsole] = useState(false);
  const [consoleHeight, setConsoleHeight] = useState(200);

  // check if config is changed
  useEffect(() => setIsConfigUpdated(props.resource.mainClass !== mainClass || props.resource.description !== description || props.resource.version !== version), [mainClass, description, version, props.resource]);

  // use unsaved changes warning
  useUnSavedChangesWarning(
    (Object.keys(originalContents).length > 0 && Object.keys(originalContents).filter(key => originalContents[key] !== contents[key]).length > 0)
    || isConfigUpdated
  );

  // load code from s3
  useEffect(() => {
    if (props.resource) {
      (async () => {
        let newContents: { [key: string]: string } = {};
        // function to load files in a folder
        const loadFolder = async (folder: ResourceFolder) => {
          for (const item of folder.items) {
            if ((item as ResourceFolder).items) {
              await loadFolder(item as ResourceFolder);
            } else {
              const content = String.fromCharCode(...(await getContentFromBucket(`${props.user.username}/${props.resource.id}/${item.id}`)).content);
              newContents[item.id] = content;
            }
          }
        }
        await loadFolder(copy(props.resource.root));
        setContents({ ...newContents });
        setOriginalContents({ ...newContents });
      })();
    }
  }, [props.resource]);

  // execute
  useEffect(() => {
    if (isExecuting) {
      // setup a new interval
      timerRef.current = setInterval(async () => {
        setSeconds(prev => prev + 1);
        // only execute every 5 seconds
        if (seconds % 5 === 0) {
          // load the build logs and status every 5 seconds
          const statusRes = await (await fetch(`${config.api.codebuild}/track?buildId=${buildId}`)).json();
          const logsRes = await (await fetch(`${config.api.codebuild}/logs?codebuild=${databaseName}&buildId=${buildId}`)).json();
          setLogs(getPrettyLogs(logsRes));
          if (statusRes.status !== 'IN_PROGRESS') { // succeeded or failed
            setIsExecuting(false);
            if (statusRes.status === 'SUCCEEDED') {
              await props.loadJarContent(props.user, props.resource);
            }
          }
        }
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [isExecuting]);

  useEventListener('mouseup', () => {
    setIsDragging(false);
    setVirtualFilePos({ top: -1000, left: -1000 });
    // put file to different index
    if (draggingIndex !== -1) {
      let newList = [...props.openingFiles].map(f => f.id === draggingFile?.id ? null : f);
      newList = [...newList.slice(0, draggingIndex), draggingFile, ...newList.slice(draggingIndex)].filter(f => f !== null);
      props.setOpeningFiles(newList as ResourceFile[]);
      setDraggingIndex(-1);
    }
  });

  // set virtual file button's position
  useEventListener('mousemove', (e) => {
    if (isDragging) setVirtualFilePos({ top: e.pageY + 10, left: e.pageX + 10 });
  });

  // handle key down event
  useEventListener('keydown', async (e) => {
    if (e.ctrlKey) {
      // save file
      if (e.key === 's' && props.selectedFile) {
        e.preventDefault();
        if (props.selectedFile.id === 'config') {
          await saveConfig();
        } else if (props.selectedFile.id !== 'jar') {
          setOriginalContents({ ...originalContents, [props.selectedFile.id]: contents[props.selectedFile.id] });
          await uploadToBucket(`${props.user.username}/${props.resource.id}/${props.selectedFile.id}`, contents[props.selectedFile.id]);
          await fetch(config.api.database, { method: 'PATCH', body: JSON.stringify({ table: `${databaseName}_resources`, item_key: 'id', item_key_value: props.resource.id, keys: ['lastUpdated'], values: [new Date().toISOString()] }) });
        }
      }
      // execute if Ctrl + Enter
      if (e.key === 'Enter') {
        e.preventDefault();
        await execute();
      }
    }
  });

  // file button handlers
  const handleFileMouseDown = (file: ResourceFile) => {
    setIsDragging(true);
    setDraggingFile({ ...file });
  }
  const handleFileMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, file: ResourceFile) => {
    if (isDragging) {
      const target = (e.target as HTMLElement).closest('.file-button');
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const [left, right] = [Math.round(rect.left), Math.round(rect.right)];
      const mouse = e.pageX;
      if (mouse - left < right - mouse) { // close to left
        setDraggingIndex(props.openingFiles.findIndex(f => f.id === target.id));
      } else {
        setDraggingIndex(props.openingFiles.findIndex(f => f.id === target.id) + 1);
      }
    }
  }
  // handle content changed
  const handleChange = (text: string) => {
    if (!props.selectedFile) return;
    setContents({ ...contents, [props.selectedFile?.id]: text });
  }

  // save the config information
  const saveConfig = async () => {
    props.setResource({ ...props.resource, mainClass: mainClass, description: description, version: version });
    await fetch(`${config.api.database}`, {
      method: 'PATCH', body: JSON.stringify({
        table: `${databaseName}_resources`,
        item_key: 'id',
        item_key_value: props.resource.id,
        keys: ['mainClass', 'version', 'description', 'lastUpdated'],
        values: [mainClass, version, description, new Date().toISOString()],
      })
    });
  }

  // execute the build
  const execute = async () => {
    // update buildspec and execute
    const buildspec = setupBuildSpec(props.user, props.resource);
    const buildspecPath = `${props.user.username}/${props.resource.id}/buildspec.yml`;
    await uploadToBucket(buildspecPath, buildspec);
    const res = await (await fetch(`${config.api.codebuild}/execute?codebuild=${databaseName}&buildspec=${buildspecPath}`)).json();
    setBuildId(res.buildId);
    setLogs(getPrettyLogs([]));
    setSeconds(0);
    setIsExecuting(true);
    setIsShowingConsole(true);
  }

  // terminate
  const terminate = async () => {
    setIsExecuting(false);
  }

  return (
    <>
      <div className='flex-1 overflow-hidden flex flex-col'>
        {/* navigator */}
        <div className='border-b w-full h-8 scroller flex' style={{ borderBottom: '1px solid #fff2' }}>
          <div className='scroller scroller-small h-full flex flex-1'>
            {props.openingFiles.map((file, i) => <div style={{ borderLeftColor: draggingIndex === i ? '#fff' : 'transparent', borderRightColor: draggingIndex === i + 1 ? '#fff' : 'transparent' }} id={file.id} onMouseMove={(e) => handleFileMouseMove(e, file)} onMouseDown={() => handleFileMouseDown(file)} className={`file-button ${props.selectedFile?.id === file.id ? 'bg-neutral-850' : ''} select-none flex px-1.5 min-w-48 h-full text-xs items-center border-x border-transparent`} key={`opening-${file.id}`}>
              <div className={`h-5 w-5 ${file.id === 'config' ? 'p-1.5' : 'p-1'} rounded-full flex items-center justify-center mr-1`}>{file.id === 'config' ? <Settings /> : extensionToIcon[file.name.split('.').pop() || ''] || <FileIcon />}</div>
              <div className='flex-1 overflow-hidden text-ellipsis cursor-pointer h-full flex items-center' onClick={() => props.setSelectedFile(file)}>{file.name}</div>
              <button className='p-0.5 w-4 aspect-square flex items-center justify-center rounded-sm hover:bg-neutral-800' onMouseEnter={() => setHoverCloseButton(file.id)} onMouseLeave={() => setHoverCloseButton('')} onClick={() => props.closeFile(file.id)}>{(contents[file.id] !== originalContents[file.id] || (file.id === 'config' && isConfigUpdated)) && hoverCloseButton !== file.id ? <div className='rounded-full w-2 aspect-square' style={{ background: '#fffc' }} /> : <CloseIcon />}</button>
            </div>)}
          </div>
          {/* action buttons */}
          <div className='px-2 flex items-center' style={{ boxShadow: '-1px 0px 3px 2px #0004' }}>
            <button onClick={execute} className='h-5 aspect-square rounded hover:bg-gray-800 p-1' disabled={isExecuting}><ExecuteIcon color={isExecuting ? '#646464' : '#10b981'} /></button>
            <button onClick={terminate} className='h-5 aspect-square rounded hover:bg-gray-800 p-1 ml-2' disabled={!isExecuting}><TerminateIcon color={isExecuting ? '#db6456' : '#646464'} /></button>
            <button className={`h-5 aspect-square rounded flex justify-center ${isShowingConsole ? 'bg-gray-700' : ''} hover:bg-gray-700 p-1 ml-2`} onClick={() => setIsShowingConsole(!isShowingConsole)}><TerminalIcon /></button>
          </div>
        </div>
        <div className='flex-1 flex flex-col justify-end scroller'>
          {isShowingConsole ?
            <>
              {/* code */}
              {props.openingFiles.map(file => <div className={`bg-neutral-850 flex-1 flex flex-col ${props.selectedFile?.id === file.id ? 'scroller' : 'hidden'}`}>
                {file.id === 'config' ?
                  <ConfigSettings saveConfig={saveConfig} resource={props.resource} setResource={props.setResource} mainClass={mainClass} setMainClass={setMainClass} description={description} setDescription={setDescription} version={version} setVersion={setVersion} />
                  : file.id === 'jar' && props.jarContent ?
                    <JarDownloadContent fileName={`${props.resource.mainClass}.jar`} content={props.jarContent} />
                    : <CodeEditor source={contents[file.id]} fileName={file.name} handleChange={handleChange} />}
              </div>)}
              {/* console */}
              <div ref={consoleRef} className='relative flex flex-col border-t' style={{ borderColor: '#fff2', height: consoleHeight }}>
                <Resizer ref={resizerRef} setHeight={setConsoleHeight} containerRef={consoleRef} />
                <div className='select-none text-xs p-1 flex cursor-default justify-between'>
                  <div className='flex'>
                    <div className='border-b border-b-emerald-500 p-1'>Terminal</div>
                  </div>
                  <div className='flex items-center mx-2'>
                    <button className='p-0.5 w-4 aspect-square hover:bg-neutral-800 rounded' onClick={() => setIsShowingConsole(false)}><CloseIcon /></button>
                  </div>
                </div>
                <div className='scroller flex-1'>
                  {logs.length > 0 && <div className={`text-xs mt-1 px-2 font-bold`}>{seconds}s<br/></div>}
                  {logs.map((log, i) => <div className={`text-xs mt-1 px-2`} style={{ color: log.color, fontWeight: log.fontStyle }} key={`log-${i}`}>{log.message === '\n' ? <br /> : log.message}</div>)}
                  <div className='h-4' />
                </div>
              </div>
            </>
            :
            props.openingFiles.map(file => <div className={`bg-neutral-850 text-red flex-1 flex flex-col ${props.selectedFile?.id === file.id ? 'scroller' : 'hidden'}`}>
              {file.id === 'config' ?
                <ConfigSettings saveConfig={saveConfig} resource={props.resource} setResource={props.setResource} mainClass={mainClass} setMainClass={setMainClass} description={description} setDescription={setDescription} version={version} setVersion={setVersion} />
                : file.id === 'jar' && props.jarContent ?
                  <JarDownloadContent fileName={`${props.resource.mainClass}.jar`} content={props.jarContent} />
                  : <CodeEditor source={contents[file.id]} fileName={file.name} handleChange={handleChange} />}
            </div>)
          }
        </div>
      </div>

      {/* virtual file button */}
      {isDragging && <div className='select-none flex px-1.5 min-w-40 h-8 bg-neutral-900 text-xs items-center border fixed z-30' style={{ opacity: .8, top: virtualFilePos.top, left: virtualFilePos.left }}>
        <div className='flex-1 overflow-hidden text-ellipsis h-full flex items-center'>{draggingFile?.name}</div>
      </div>}
    </>
  )
}

export default ResourceEditor